import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { compressImage } from '../utils/imageCompression';
import { ArrowLeft, Camera, Cloud, Edit, Plus, Printer, Save, Trash, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS220Form = ({ currentExercise, theme }) => {
  const [view, setView] = useState('list');
  const [summaries, setSummaries] = useState([]);
  const [editingSummary, setEditingSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    exercise_name: '',
    date_from: '',
    date_to: '',
    remarks: '',
    // NOTAM fields
    radius_nm: '',
    altitude_asl: '',
    center_point: '',
    latitude: '',
    longitude: '',
    sunrise: '',
    sunset: '',
    // Dynamic arrays
    personnel: [],
    fixed_wing_aircraft: [],
    helicopters: [],
    drones: []
  });

  // Load summaries from backend API
  const loadSummaries = async () => {
    if (!currentExercise?.id) return;
    
    try {
      setLoading(true);
      const token = secureStorage.getToken();
      const response = await axios.get(
        `${API}/ics-220?exercise_id=${currentExercise.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummaries(response.data);
    } catch (error) {
      console.error('Error loading ICS 220 summaries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummaries();
  }, [currentExercise?.id]);

  // Set exercise name when currentExercise changes
  useEffect(() => {
    if (currentExercise?.exercise_name) {
      setFormData(prev => ({
        ...prev,
        exercise_name: currentExercise.exercise_name
      }));
    }
  }, [currentExercise]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate latitude and longitude
  const validateCoordinates = () => {
    const newErrors = {};
    
    if (formData.latitude) {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        newErrors.latitude = 'Latitude must be between -90.0000 and 90.0000';
      }
    }
    
    if (formData.longitude) {
      const lon = parseFloat(formData.longitude);
      if (isNaN(lon) || lon < -180 || lon > 180) {
        newErrors.longitude = 'Longitude must be between -180.0000 and 180.0000';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Personnel management
  const addPersonnel = () => {
    setFormData(prev => ({
      ...prev,
      personnel: [...prev.personnel, {
        id: Date.now(),
        name: '',
        phone: '',
        frequencies: '',
        am: '',
        fm: ''
      }]
    }));
  };

  const updatePersonnel = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      personnel: prev.personnel.map(p => 
        p.id === id ? { ...p, [field]: value } : p
      )
    }));
  };

  const removePersonnel = (id) => {
    setFormData(prev => ({
      ...prev,
      personnel: prev.personnel.filter(p => p.id !== id)
    }));
  };

  // Fixed Wing Aircraft management
  const addFixedWing = () => {
    setFormData(prev => ({
      ...prev,
      fixed_wing_aircraft: [...prev.fixed_wing_aircraft, {
        id: Date.now(),
        registration: '',
        make_model: '',
        base: '',
        start: '',
        remarks: '',
        tasking: '',
        capabilities: '',
        military: false,
        civilian: false,
        medevac: false,
        casevac: false,
        search_rescue: false,
        stars: false,
        image: null
      }]
    }));
  };

  const updateFixedWing = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      fixed_wing_aircraft: prev.fixed_wing_aircraft.map(fw => 
        fw.id === id ? { ...fw, [field]: value } : fw
      )
    }));
  };

  const removeFixedWing = (id) => {
    setFormData(prev => ({
      ...prev,
      fixed_wing_aircraft: prev.fixed_wing_aircraft.filter(fw => fw.id !== id)
    }));
  };

  // Helicopters management
  const addHelicopter = () => {
    setFormData(prev => ({
      ...prev,
      helicopters: [...prev.helicopters, {
        id: Date.now(),
        registration: '',
        make_model: '',
        base: '',
        start: '',
        remarks: '',
        tasking: '',
        capabilities: '',
        military: false,
        civilian: false,
        medevac: false,
        casevac: false,
        search_rescue: false,
        stars: false,
        image: null
      }]
    }));
  };

  const updateHelicopter = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      helicopters: prev.helicopters.map(h => 
        h.id === id ? { ...h, [field]: value } : h
      )
    }));
  };

  const removeHelicopter = (id) => {
    setFormData(prev => ({
      ...prev,
      helicopters: prev.helicopters.filter(h => h.id !== id)
    }));
  };

  // Drones — same shape as Helicopters; separate list so they don't cross-pollinate
  const addDrone = () => {
    setFormData(prev => ({
      ...prev,
      drones: [...(prev.drones || []), {
        id: Date.now(),
        registration: '',
        make_model: '',
        base: '',
        start: '',
        remarks: '',
        tasking: '',
        capabilities: '',
        military: false,
        civilian: false,
        medevac: false,
        casevac: false,
        search_rescue: false,
        stars: false,
        image: null,
      }]
    }));
  };

  const updateDrone = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      drones: (prev.drones || []).map(d => d.id === id ? { ...d, [field]: value } : d)
    }));
  };

  const removeDrone = (id) => {
    setFormData(prev => ({
      ...prev,
      drones: (prev.drones || []).filter(d => d.id !== id)
    }));
  };

  const handleDroneImageUpload = async (id, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg','image/jpg','image/png','image/gif','image/heic','image/webp'];
    if (!validTypes.includes(file.type)) { alert('Please select a valid image file (JPEG, PNG, GIF, HEIC, or WebP)'); return; }
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const compressed = await compressImage(reader.result, 800, 0.75);
        updateDrone(id, 'image', compressed);
      } catch (e) { console.error(e); alert('Error processing image. Please try again.'); }
    };
    reader.readAsDataURL(file);
  };

  const handleDroneCameraCapture = async (id) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const video = document.createElement('video');
      video.srcObject = stream; video.autoplay = true; video.playsInline = true;
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;';
      video.style.cssText = 'max-width:90%;max-height:70%;';
      modal.appendChild(video);
      const btnRow = document.createElement('div');
      btnRow.style.cssText = 'margin-top:20px;display:flex;gap:10px;';
      const cap = document.createElement('button');
      cap.textContent = '📷 Capture';
      cap.style.cssText = 'padding:10px 20px;background:#10b981;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;';
      cap.onclick = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth; canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        try {
          const compressed = await compressImage(canvas.toDataURL('image/jpeg', 0.85), 800, 0.75);
          updateDrone(id, 'image', compressed);
        } catch (err) { console.error(err); }
        stream.getTracks().forEach(t => t.stop());
        document.body.removeChild(modal);
      };
      const cancel = document.createElement('button');
      cancel.textContent = '✕ Cancel';
      cancel.style.cssText = 'padding:10px 20px;background:#ef4444;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;';
      cancel.onclick = () => { stream.getTracks().forEach(t => t.stop()); document.body.removeChild(modal); };
      btnRow.appendChild(cap); btnRow.appendChild(cancel); modal.appendChild(btnRow);
      document.body.appendChild(modal);
    } catch (err) { alert('Camera unavailable: ' + err.message); }
  };

  // Image compression utility function for aircraft images (800x600, max 300KB)
  const compressImage = (imageDataUrl, maxWidth = 800, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          const maxHeight = 600;
          
          // Scale down to fit within max dimensions
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio, 1); // Don't upscale
          
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Use better quality settings
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to compressed JPEG (targets max 300KB)
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Log compression results
          console.log(`Aircraft image compressed to ${width}x${height}`);
          
          resolve(compressedDataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      img.src = imageDataUrl;
    });
  };

  // Image handling functions
  const handleFixedWingImageUpload = async (id, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/heic', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, HEIC, or WebP)');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          // Compress the image with resource/aircraft settings (800x600, 75% quality)
          const compressedImage = await compressImage(reader.result, 800, 0.75);
          updateFixedWing(id, 'image', compressedImage);
        } catch (error) {
          console.error('Error compressing image:', error);
          alert('Error processing image. Please try again.');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFixedWingCameraCapture = async (id) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;
      
      // Create modal overlay
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;';
      
      // Add video to modal
      video.style.cssText = 'max-width:90%;max-height:70%;';
      modal.appendChild(video);
      
      // Create button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = 'margin-top:20px;display:flex;gap:10px;';
      
      // Capture button
      const captureBtn = document.createElement('button');
      captureBtn.textContent = '📷 Capture';
      captureBtn.style.cssText = 'padding:10px 20px;background:#10b981;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;';
      captureBtn.onclick = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg', 0.75);
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(modal);
        
        try {
          const compressedImage = await compressImage(imageData, 800, 0.75);
          updateFixedWing(id, 'image', compressedImage);
        } catch (error) {
          console.error('Error compressing image:', error);
          alert('Error processing captured photo');
        }
      };
      
      // Cancel button
      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = '✕ Cancel';
      cancelBtn.style.cssText = 'padding:10px 20px;background:#ef4444;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;';
      cancelBtn.onclick = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(modal);
      };
      
      buttonContainer.appendChild(captureBtn);
      buttonContainer.appendChild(cancelBtn);
      modal.appendChild(buttonContainer);
      document.body.appendChild(modal);
      
    } catch (error) {
      console.error('Camera error:', error);
      alert('Unable to access camera. Please check permissions or try uploading an image instead.');
    }
  };

  const handleHelicopterImageUpload = async (id, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/heic', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, HEIC, or WebP)');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          // Compress the image with resource/aircraft settings (800x600, 75% quality)
          const compressedImage = await compressImage(reader.result, 800, 0.75);
          updateHelicopter(id, 'image', compressedImage);
        } catch (error) {
          console.error('Error compressing image:', error);
          alert('Error processing image. Please try again.');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHelicopterCameraCapture = async (id) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;
      
      // Create modal overlay
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;';
      
      // Add video to modal
      video.style.cssText = 'max-width:90%;max-height:70%;';
      modal.appendChild(video);
      
      // Create button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = 'margin-top:20px;display:flex;gap:10px;';
      
      // Capture button
      const captureBtn = document.createElement('button');
      captureBtn.textContent = '📷 Capture';
      captureBtn.style.cssText = 'padding:10px 20px;background:#10b981;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;';
      captureBtn.onclick = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg', 0.75);
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(modal);
        
        try {
          const compressedImage = await compressImage(imageData, 800, 0.75);
          updateHelicopter(id, 'image', compressedImage);
        } catch (error) {
          console.error('Error compressing image:', error);
          alert('Error processing captured photo');
        }
      };
      
      // Cancel button
      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = '✕ Cancel';
      cancelBtn.style.cssText = 'padding:10px 20px;background:#ef4444;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;';
      cancelBtn.onclick = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(modal);
      };
      
      buttonContainer.appendChild(captureBtn);
      buttonContainer.appendChild(cancelBtn);
      modal.appendChild(buttonContainer);
      document.body.appendChild(modal);
      
    } catch (error) {
      console.error('Camera error:', error);
      alert('Unable to access camera. Please check permissions or try uploading an image instead.');
    }
  };

  const handleSave = async () => {
    if (!validateCoordinates()) {
      return;
    }

    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to save');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const summaryData = {
        exercise_id: currentExercise?.id || '',
        ...formData
      };

      if (editingSummary) {
        await axios.put(
          `${API}/ics-220/${editingSummary.id}`,
          summaryData,
          { headers }
        );
      } else {
        await axios.post(`${API}/ics-220`, summaryData, { headers });
      }

      await loadSummaries();
      setView('list');
      setEditingSummary(null);
      resetForm();
    } catch (error) {
      console.error('Error saving ICS 220:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error saving ICS 220: ${errorMessage}`);
    }
  };

  const handleEdit = (summary) => {
    setEditingSummary(summary);
    setFormData({
      exercise_name: summary.exercise_name || '',
      date_from: summary.date_from || '',
      date_to: summary.date_to || '',
      remarks: summary.remarks || '',
      radius_nm: summary.radius_nm || '',
      altitude_asl: summary.altitude_asl || '',
      center_point: summary.center_point || '',
      latitude: summary.latitude || '',
      longitude: summary.longitude || '',
      sunrise: summary.sunrise || '',
      sunset: summary.sunset || '',
      personnel: summary.personnel || [],
      fixed_wing_aircraft: summary.fixed_wing_aircraft || [],
      helicopters: summary.helicopters || [],
      drones: summary.drones || []
    });
    setView('add');
  };

  const handleDelete = async (summaryId) => {
    if (!window.confirm('Are you sure you want to delete this air operations summary?')) {
      return;
    }

    try {
      const token = secureStorage.getToken();
      if (!token) {
        alert('Please log in to delete');
        return;
      }

      await axios.delete(`${API}/ics-220/${summaryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await loadSummaries();
    } catch (error) {
      console.error('Error deleting ICS 220:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error deleting ICS 220: ${errorMessage}`);
    }
  };

  const resetForm = () => {
    setFormData({
      exercise_name: currentExercise?.exercise_name || '',
      date_from: '',
      date_to: '',
      remarks: '',
      radius_nm: '',
      altitude_asl: '',
      center_point: '',
      latitude: '',
      longitude: '',
      sunrise: '',
      sunset: '',
      personnel: [],
      fixed_wing_aircraft: [],
      helicopters: [],
      drones: []
    });
    setErrors({});
  };

  const printICS220 = (summary) => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>ICS 220 - Air Operations Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; }
            .header { text-align: center; border-bottom: 2px solid #333; margin-bottom: 15px; padding-bottom: 10px; }
            .form-section { margin-bottom: 15px; padding: 10px; border: 1px solid #333; }
            .section-title { font-weight: bold; font-size: 13px; margin-bottom: 10px; background-color: #f0f0f0; padding: 5px; }
            .field-row { display: flex; gap: 15px; margin-bottom: 8px; }
            .field { flex: 1; }
            .field-label { font-weight: bold; font-size: 10px; margin-bottom: 3px; }
            .field-value { border-bottom: 1px solid #333; min-height: 18px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #333; padding: 6px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .notam-section { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
            .footer { position: fixed; bottom: 20px; left: 20px; right: 20px; text-align: center; font-size: 9px; border-top: 1px solid #333; padding-top: 10px; }
            @media print {
              @page { margin: 15px; }
              body { margin: 0; padding: 15px; }
              .footer { position: fixed; bottom: 15px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 220 - Air Operations Summary</h1>
          </div>
          
          <div class="form-section">
            <div class="field-row">
              <div class="field">
                <div class="field-label">Exercise/Incident Name:</div>
                <div class="field-value">${summary.exercise_name || 'N/A'}</div>
              </div>
              <div class="field">
                <div class="field-label">Operational Period:</div>
                <div class="field-value">From: ${summary.date_from ? new Date(summary.date_from).toLocaleString() : 'N/A'} To: ${summary.date_to ? new Date(summary.date_to).toLocaleString() : 'N/A'}</div>
              </div>
            </div>
            <div class="field-row">
              <div class="field">
                <div class="field-label">Remarks:</div>
                <div class="field-value">${summary.remarks || ''}</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-title">NOTAM</div>
            <div class="notam-section">
              <div>
                <div class="field-label">Radius (nm):</div>
                <div class="field-value">${summary.radius_nm || ''}</div>
                <div class="field-label" style="margin-top: 8px;">Altitude ASL:</div>
                <div class="field-value">${summary.altitude_asl || ''}</div>
                <div class="field-label" style="margin-top: 8px;">Center Point:</div>
                <div class="field-value">${summary.center_point || ''}</div>
                <div class="field-label" style="margin-top: 8px;">Latitude:</div>
                <div class="field-value">${summary.latitude || ''}</div>
              </div>
              <div>
                <div class="field-label">Longitude:</div>
                <div class="field-value">${summary.longitude || ''}</div>
                <div class="field-label" style="margin-top: 8px;">Sunrise:</div>
                <div class="field-value">${summary.sunrise || ''}</div>
                <div class="field-label" style="margin-top: 8px;">Sunset:</div>
                <div class="field-value">${summary.sunset || ''}</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-title">Personnel</div>
            ${summary.personnel && summary.personnel.length > 0 ? `
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone #</th>
                    <th>Frequencies</th>
                    <th>AM</th>
                    <th>FM</th>
                  </tr>
                </thead>
                <tbody>
                  ${summary.personnel.map(p => `
                    <tr>
                      <td>${p.name || ''}</td>
                      <td>${p.phone || ''}</td>
                      <td>${p.frequencies || ''}</td>
                      <td>${p.am || ''}</td>
                      <td>${p.fm || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : '<p style="font-style:italic;color:#666;">No personnel entered.</p>'}
          </div>

          ${summary.fixed_wing_aircraft && summary.fixed_wing_aircraft.length > 0 ? `
            <div class="form-section">
              <div class="section-title">Fixed Wing Aircraft</div>
              <table>
                <thead>
                  <tr>
                    <th>Registration</th>
                    <th>Make/Model</th>
                    <th>Base</th>
                    <th>Start</th>
                    <th>Remarks</th>
                    <th>Tasking</th>
                    <th>Capabilities</th>
                    <th>Military</th>
                    <th>Civilian</th>
                    <th>Medevac</th>
                    <th>Casevac</th>
                    <th>Search & Rescue</th>
                    <th>STARS</th>
                  </tr>
                </thead>
                <tbody>
                  ${summary.fixed_wing_aircraft.map(fw => `
                    <tr>
                      <td>${fw.registration || ''}</td>
                      <td>${fw.make_model || ''}</td>
                      <td>${fw.base || ''}</td>
                      <td>${fw.start ? new Date(fw.start).toLocaleString() : ''}</td>
                      <td>${fw.remarks || ''}</td>
                      <td>${fw.tasking || ''}</td>
                      <td>${fw.capabilities || ''}</td>
                      <td>${fw.military ? '✓' : ''}</td>
                      <td>${fw.civilian ? '✓' : ''}</td>
                      <td>${fw.medevac ? '✓' : ''}</td>
                      <td>${fw.casevac ? '✓' : ''}</td>
                      <td>${fw.search_rescue ? '✓' : ''}</td>
                      <td>${fw.stars ? '✓' : ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          ${summary.helicopters && summary.helicopters.length > 0 ? `
            <div class="form-section">
              <div class="section-title">Helicopters</div>
              <table>
                <thead>
                  <tr>
                    <th>Registration</th>
                    <th>Make/Model</th>
                    <th>Base</th>
                    <th>Start</th>
                    <th>Remarks</th>
                    <th>Tasking</th>
                    <th>Capabilities</th>
                    <th>Military</th>
                    <th>Civilian</th>
                    <th>Medevac</th>
                    <th>Casevac</th>
                    <th>Search & Rescue</th>
                    <th>STARS</th>
                  </tr>
                </thead>
                <tbody>
                  ${summary.helicopters.map(h => `
                    <tr>
                      <td>${h.registration || ''}</td>
                      <td>${h.make_model || ''}</td>
                      <td>${h.base || ''}</td>
                      <td>${h.start ? new Date(h.start).toLocaleString() : ''}</td>
                      <td>${h.remarks || ''}</td>
                      <td>${h.tasking || ''}</td>
                      <td>${h.capabilities || ''}</td>
                      <td>${h.military ? '✓' : ''}</td>
                      <td>${h.civilian ? '✓' : ''}</td>
                      <td>${h.medevac ? '✓' : ''}</td>
                      <td>${h.casevac ? '✓' : ''}</td>
                      <td>${h.search_rescue ? '✓' : ''}</td>
                      <td>${h.stars ? '✓' : ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          ${summary.drones && summary.drones.length > 0 ? `
            <div class="form-section">
              <div class="section-title">Drones</div>
              <table>
                <thead>
                  <tr>
                    <th>Registration</th>
                    <th>Make/Model</th>
                    <th>Base</th>
                    <th>Start</th>
                    <th>Remarks</th>
                    <th>Tasking</th>
                    <th>Capabilities</th>
                    <th>Military</th>
                    <th>Civilian</th>
                    <th>Medevac</th>
                    <th>Casevac</th>
                    <th>Search & Rescue</th>
                    <th>STARS</th>
                  </tr>
                </thead>
                <tbody>
                  ${summary.drones.map(d => `
                    <tr>
                      <td>${d.registration || ''}</td>
                      <td>${d.make_model || ''}</td>
                      <td>${d.base || ''}</td>
                      <td>${d.start ? new Date(d.start).toLocaleString() : ''}</td>
                      <td>${d.remarks || ''}</td>
                      <td>${d.tasking || ''}</td>
                      <td>${d.capabilities || ''}</td>
                      <td>${d.military ? '✓' : ''}</td>
                      <td>${d.civilian ? '✓' : ''}</td>
                      <td>${d.medevac ? '✓' : ''}</td>
                      <td>${d.casevac ? '✓' : ''}</td>
                      <td>${d.search_rescue ? '✓' : ''}</td>
                      <td>${d.stars ? '✓' : ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <div class="footer">
            ICS 220 - Air Operations Summary | Generated on ${new Date().toLocaleString()} | EXRSIM Emergency Management Platform
          </div>
        </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // List View
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>ICS 220 - Air Operations Summary</h2>
            <p className={`${theme.colors.textMuted} mt-1`}>Manage air operations summaries for incident response</p>
          </div>
          <Button 
            onClick={() => {
              resetForm();
              setView('add');
            }}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New ICS 220
          </Button>
        </div>

        {summaries.length === 0 ? (
          <div className={`text-center py-12 ${theme.colors.card} rounded-lg border ${theme.colors.border}`}>
            <Cloud className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No air operations summaries yet</p>
            <Button onClick={() => {
              resetForm();
              setView('add');
            }} className="bg-orange-600 hover:bg-orange-700">
              Create First ICS 220
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {summaries.map((summary) => (
              <div key={summary.id} className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-4`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className={`font-semibold ${theme.colors.textPrimary}`}>{summary.exercise_name || 'Untitled'}</h3>
                    <p className={`text-sm ${theme.colors.textMuted}`}>
                      Period: {summary.date_from ? new Date(summary.date_from).toLocaleString() : 'N/A'} to {summary.date_to ? new Date(summary.date_to).toLocaleString() : 'N/A'}
                    </p>
                    <p className={`text-sm ${theme.colors.textMuted}`}>
                      Personnel: {summary.personnel?.length || 0} | Fixed Wing: {summary.fixed_wing_aircraft?.length || 0} | Helicopters: {summary.helicopters?.length || 0} | Drones: {summary.drones?.length || 0}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => printICS220(summary)}
                      className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Print
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(summary)}
                      className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(summary.id)}
                      className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Add/Edit View
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>
            {editingSummary ? 'Edit' : 'Create'} ICS 220 - Air Operations Summary
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingSummary(null);
              resetForm();
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          {editingSummary && (
            <Button
              variant="outline"
              onClick={() => printICS220(formData)}
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print ICS 220
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Header Information Card */}
        <div className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-orange-500">Header Information</h3>
            <Button
              onClick={handleSave}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Header
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-white">Exercise/Incident Name</Label>
              <Input
                value={formData.exercise_name}
                onChange={(e) => handleInputChange('exercise_name', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                readOnly
              />
            </div>
            
            <div className="text-right">
              <Label className="text-white">Operational Period</Label>
              <div className="text-white text-sm mt-2">(From - To)</div>
            </div>
            
            <div>
              <Label className="text-white">Date/Time From:</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                  onChange={(e) => {
                    const currentTime = formData.date_from ? formData.date_from.split('T')[1] || '00:00' : '00:00';
                    handleInputChange('date_from', `${e.target.value}T${currentTime}`);
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <Input
                  type="text"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  placeholder="HH:MM (24-hour)"
                  maxLength="5"
                  value={formData.date_from ? formData.date_from.split('T')[1] || '' : ''}
                  onChange={(e) => {
                    const currentDate = formData.date_from ? formData.date_from.split('T')[0] || '' : '';
                    handleInputChange('date_from', `${currentDate}T${e.target.value}`);
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white">Date/Time To:</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                  onChange={(e) => {
                    const currentTime = formData.date_to ? formData.date_to.split('T')[1] || '00:00' : '00:00';
                    handleInputChange('date_to', `${e.target.value}T${currentTime}`);
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <Input
                  type="text"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  placeholder="HH:MM (24-hour)"
                  maxLength="5"
                  value={formData.date_to ? formData.date_to.split('T')[1] || '' : ''}
                  onChange={(e) => {
                    const currentDate = formData.date_to ? formData.date_to.split('T')[0] || '' : '';
                    handleInputChange('date_to', `${currentDate}T${e.target.value}`);
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white">Remarks</Label>
              <Textarea
                value={formData.remarks}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
                placeholder="safety notes, hazards, etc"
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                rows={3}
              />
            </div>
          </div>

          {/* NOTAM Section */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-md font-semibold text-orange-500">NOTAM</h4>
              <Button
                onClick={handleSave}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Save NOTAM
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <Label className="text-white">Radius nm</Label>
                  <Input
                    type="number"
                    value={formData.radius_nm}
                    onChange={(e) => handleInputChange('radius_nm', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Altitude ASL</Label>
                  <Input
                    type="number"
                    value={formData.altitude_asl}
                    onChange={(e) => handleInputChange('altitude_asl', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Center Point</Label>
                  <Input
                    value={formData.center_point}
                    onChange={(e) => handleInputChange('center_point', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-white">Sunrise</Label>
                  <Input
                    type="text" pattern="[0-2][0-9]:[0-5][0-9]" placeholder="HH:MM (24-hour)" maxLength="5"
                    value={formData.sunrise}
                    onChange={(e) => handleInputChange('sunrise', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Sunset</Label>
                  <Input
                    type="text" pattern="[0-2][0-9]:[0-5][0-9]" placeholder="HH:MM (24-hour)" maxLength="5"
                    value={formData.sunset}
                    onChange={(e) => handleInputChange('sunset', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Latitude and Longitude on same row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <Label className="text-white">Latitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.latitude}
                  onChange={(e) => handleInputChange('latitude', e.target.value)}
                  onBlur={validateCoordinates}
                  placeholder="47.0000"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
                {errors.latitude && (
                  <p className="text-red-500 text-sm mt-1">{errors.latitude}</p>
                )}
              </div>
              <div>
                <Label className="text-white">Longitude</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                  onBlur={validateCoordinates}
                  placeholder="-97.0000"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
                {errors.longitude && (
                  <p className="text-red-500 text-sm mt-1">{errors.longitude}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Personnel Card */}
        <div className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-orange-500">Personnel</h3>
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Personnel
              </Button>
              <Button
                onClick={addPersonnel}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Personnel
              </Button>
            </div>
          </div>

          {formData.personnel.length === 0 ? (
            <p className={`${theme.colors.textMuted} text-center py-4`}>No personnel added yet</p>
          ) : (
            <div className="space-y-3">
              {formData.personnel.map((person) => (
                <div key={person.id} className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end p-3 bg-gray-800/50 rounded">
                  <div>
                    <Label className="text-white text-xs">Name</Label>
                    <Input
                      value={person.name}
                      onChange={(e) => updatePersonnel(person.id, 'name', e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Phone #</Label>
                    <Input
                      value={person.phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow only digits, dashes, spaces, parentheses, and plus sign
                        const sanitized = value.replace(/[^\d\-\s()+ ]/g, '');
                        updatePersonnel(person.id, 'phone', sanitized);
                      }}
                      placeholder="705-123-4567"
                      pattern="[\d\-\s()+ ]+"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Frequencies</Label>
                    <Input
                      value={person.frequencies}
                      onChange={(e) => updatePersonnel(person.id, 'frequencies', e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">AM</Label>
                    <Input
                      value={person.am}
                      onChange={(e) => updatePersonnel(person.id, 'am', e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">FM</Label>
                    <Input
                      value={person.fm}
                      onChange={(e) => updatePersonnel(person.id, 'fm', e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSave}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      title="Save changes"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => removePersonnel(person.id)}
                      size="sm"
                      variant="outline"
                      className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                      title="Delete entry"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fixed Wing Aircraft Card */}
        <div className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-orange-500">Fixed Wing Aircraft</h3>
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Aircraft
              </Button>
              <Button
                onClick={addFixedWing}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Aircraft
              </Button>
            </div>
          </div>

          {formData.fixed_wing_aircraft.length === 0 ? (
            <p className={`${theme.colors.textMuted} text-center py-4`}>No fixed wing aircraft added yet</p>
          ) : (
            <div className="space-y-3">
              {formData.fixed_wing_aircraft.map((aircraft) => (
                <div key={aircraft.id} className="p-3 bg-gray-800/50 rounded">
                  <div className="flex gap-4">
                    {/* Image Holder on Left */}
                    <div className="flex-shrink-0">
                      <Label className="text-white text-xs mb-2 block">Photo</Label>
                      <div
                        className="relative w-32 h-32 bg-gray-700 rounded border-2 border-dashed border-gray-600 hover:border-orange-500/60 flex items-center justify-center overflow-hidden cursor-pointer transition-colors"
                        title="Drag & drop an image here, or click Upload"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-orange-500', 'ring-2', 'ring-orange-500'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500', 'ring-2', 'ring-orange-500'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-orange-500', 'ring-2', 'ring-orange-500');
                          const file = e.dataTransfer.files?.[0];
                          if (file) handleFixedWingImageUpload(aircraft.id, { target: { files: [file] } });
                        }}
                        data-testid={`fw-image-dropzone-${aircraft.id}`}
                      >
                        {aircraft.image ? (
                          <img src={aircraft.image} alt="Aircraft" className="w-full h-full object-cover pointer-events-none" />
                        ) : (
                          <div className="text-center pointer-events-none">
                            <Camera className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                            <span className="text-xs text-gray-400">Drag & drop<br/>or upload</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => document.getElementById(`fixed-wing-upload-${aircraft.id}`).click()}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded text-xs flex items-center justify-center gap-1"
                          title="Upload from gallery"
                        >
                          <Upload className="h-3 w-3" />
                          Upload
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFixedWingCameraCapture(aircraft.id)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded text-xs flex items-center justify-center gap-1"
                          title="Take photo with camera"
                        >
                          <Camera className="h-3 w-3" />
                          Camera
                        </button>
                      </div>
                      <input
                        id={`fixed-wing-upload-${aircraft.id}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFixedWingImageUpload(aircraft.id, e)}
                        className="hidden"
                      />
                    </div>
                    
                    {/* Form Fields on Right */}
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
                        <div>
                          <Label className="text-white text-xs">Registration</Label>
                          <Input
                            value={aircraft.registration}
                            onChange={(e) => updateFixedWing(aircraft.id, 'registration', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Make/Model</Label>
                          <Input
                            value={aircraft.make_model}
                            onChange={(e) => updateFixedWing(aircraft.id, 'make_model', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Base</Label>
                          <Input
                            value={aircraft.base}
                            onChange={(e) => updateFixedWing(aircraft.id, 'base', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Start Date</Label>
                          <Input
                            type="date"
                            value={aircraft.start ? aircraft.start.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = aircraft.start ? aircraft.start.split('T')[1] : '00:00';
                              updateFixedWing(aircraft.id, 'start', `${date}T${time}`);
                            }}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Start Time</Label>
                          <Input
                            type="text"
                            placeholder="23:35"
                            pattern="^([01]\d|2[0-3]):?([0-5]\d)$"
                            maxLength="5"
                            value={aircraft.start ? aircraft.start.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = aircraft.start ? aircraft.start.split('T')[0] : '';
                                updateFixedWing(aircraft.id, 'start', `${date}T${value}`);
                              }
                            }}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Remarks</Label>
                          <Input
                            value={aircraft.remarks}
                            onChange={(e) => updateFixedWing(aircraft.id, 'remarks', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-white text-xs">Tasking</Label>
                          <Input
                            value={aircraft.tasking || ''}
                            onChange={(e) => updateFixedWing(aircraft.id, 'tasking', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white w-full"
                            data-testid={`fw-tasking-${aircraft.id}`}
                          />
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-white text-xs">Capabilities</Label>
                          <Input
                            value={aircraft.capabilities || ''}
                            onChange={(e) => updateFixedWing(aircraft.id, 'capabilities', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white w-full"
                            data-testid={`fw-capabilities-${aircraft.id}`}
                          />
                        </div>
                      </div>
                      
                      <div className="flex gap-4 flex-wrap items-center justify-between">
                        <div className="flex gap-4 flex-wrap">
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={aircraft.military}
                              onCheckedChange={(checked) => updateFixedWing(aircraft.id, 'military', checked)}
                            />
                            Military
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={aircraft.civilian}
                              onCheckedChange={(checked) => updateFixedWing(aircraft.id, 'civilian', checked)}
                            />
                            Civilian
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={aircraft.stars}
                              onCheckedChange={(checked) => updateFixedWing(aircraft.id, 'stars', checked)}
                            />
                            STARS
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={aircraft.medevac}
                              onCheckedChange={(checked) => updateFixedWing(aircraft.id, 'medevac', checked)}
                            />
                            Medevac
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={aircraft.casevac}
                              onCheckedChange={(checked) => updateFixedWing(aircraft.id, 'casevac', checked)}
                            />
                            Casevac
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={aircraft.search_rescue}
                              onCheckedChange={(checked) => updateFixedWing(aircraft.id, 'search_rescue', checked)}
                            />
                            Search and Rescue
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-700">
                        <Button
                          onClick={handleSave}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          title="Save changes"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button
                          onClick={() => removeFixedWing(aircraft.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                          title="Delete entry"
                        >
                          <Trash className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Helicopters Card */}
        <div className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-orange-500">Helicopters</h3>
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Helicopters
              </Button>
              <Button
                onClick={addHelicopter}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Helicopter
              </Button>
            </div>
          </div>

          {formData.helicopters.length === 0 ? (
            <p className={`${theme.colors.textMuted} text-center py-4`}>No helicopters added yet</p>
          ) : (
            <div className="space-y-3">
              {formData.helicopters.map((helicopter) => (
                <div key={helicopter.id} className="p-3 bg-gray-800/50 rounded">
                  <div className="flex gap-4">
                    {/* Image Holder on Left */}
                    <div className="flex-shrink-0">
                      <Label className="text-white text-xs mb-2 block">Photo</Label>
                      <div
                        className="relative w-32 h-32 bg-gray-700 rounded border-2 border-dashed border-gray-600 hover:border-orange-500/60 flex items-center justify-center overflow-hidden cursor-pointer transition-colors"
                        title="Drag & drop an image here, or click Upload"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-orange-500', 'ring-2', 'ring-orange-500'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500', 'ring-2', 'ring-orange-500'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-orange-500', 'ring-2', 'ring-orange-500');
                          const file = e.dataTransfer.files?.[0];
                          if (file) handleHelicopterImageUpload(helicopter.id, { target: { files: [file] } });
                        }}
                        data-testid={`heli-image-dropzone-${helicopter.id}`}
                      >
                        {helicopter.image ? (
                          <img src={helicopter.image} alt="Helicopter" className="w-full h-full object-cover pointer-events-none" />
                        ) : (
                          <div className="text-center pointer-events-none">
                            <Camera className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                            <span className="text-xs text-gray-400">Drag & drop<br/>or upload</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => document.getElementById(`helicopter-upload-${helicopter.id}`).click()}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded text-xs flex items-center justify-center gap-1"
                          title="Upload from gallery"
                        >
                          <Upload className="h-3 w-3" />
                          Upload
                        </button>
                        <button
                          type="button"
                          onClick={() => handleHelicopterCameraCapture(helicopter.id)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded text-xs flex items-center justify-center gap-1"
                          title="Take photo with camera"
                        >
                          <Camera className="h-3 w-3" />
                          Camera
                        </button>
                      </div>
                      <input
                        id={`helicopter-upload-${helicopter.id}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleHelicopterImageUpload(helicopter.id, e)}
                        className="hidden"
                      />
                    </div>
                    
                    {/* Form Fields on Right */}
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
                        <div>
                          <Label className="text-white text-xs">Registration</Label>
                          <Input
                            value={helicopter.registration}
                            onChange={(e) => updateHelicopter(helicopter.id, 'registration', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Make/Model</Label>
                          <Input
                            value={helicopter.make_model}
                            onChange={(e) => updateHelicopter(helicopter.id, 'make_model', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Base</Label>
                          <Input
                            value={helicopter.base}
                            onChange={(e) => updateHelicopter(helicopter.id, 'base', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Start Date</Label>
                          <Input
                            type="date"
                            value={helicopter.start ? helicopter.start.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = helicopter.start ? helicopter.start.split('T')[1] : '00:00';
                              updateHelicopter(helicopter.id, 'start', `${date}T${time}`);
                            }}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Start Time</Label>
                          <Input
                            type="text"
                            placeholder="23:35"
                            pattern="^([01]\d|2[0-3]):?([0-5]\d)$"
                            maxLength="5"
                            value={helicopter.start ? helicopter.start.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = helicopter.start ? helicopter.start.split('T')[0] : '';
                                updateHelicopter(helicopter.id, 'start', `${date}T${value}`);
                              }
                            }}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Remarks</Label>
                          <Input
                            value={helicopter.remarks}
                            onChange={(e) => updateHelicopter(helicopter.id, 'remarks', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-white text-xs">Tasking</Label>
                          <Input
                            value={helicopter.tasking || ''}
                            onChange={(e) => updateHelicopter(helicopter.id, 'tasking', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white w-full"
                            data-testid={`heli-tasking-${helicopter.id}`}
                          />
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-white text-xs">Capabilities</Label>
                          <Input
                            value={helicopter.capabilities || ''}
                            onChange={(e) => updateHelicopter(helicopter.id, 'capabilities', e.target.value)}
                            className="bg-gray-800 border-gray-700 text-white w-full"
                            data-testid={`heli-capabilities-${helicopter.id}`}
                          />
                        </div>
                      </div>
                  
                      <div className="flex gap-4 flex-wrap items-center justify-between">
                        <div className="flex gap-4 flex-wrap">
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={helicopter.military}
                              onCheckedChange={(checked) => updateHelicopter(helicopter.id, 'military', checked)}
                            />
                            Military
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={helicopter.civilian}
                              onCheckedChange={(checked) => updateHelicopter(helicopter.id, 'civilian', checked)}
                            />
                            Civilian
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={helicopter.stars}
                              onCheckedChange={(checked) => updateHelicopter(helicopter.id, 'stars', checked)}
                            />
                            STARS
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={helicopter.medevac}
                              onCheckedChange={(checked) => updateHelicopter(helicopter.id, 'medevac', checked)}
                            />
                            Medevac
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={helicopter.casevac}
                              onCheckedChange={(checked) => updateHelicopter(helicopter.id, 'casevac', checked)}
                            />
                            Casevac
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox
                              checked={helicopter.search_rescue}
                              onCheckedChange={(checked) => updateHelicopter(helicopter.id, 'search_rescue', checked)}
                            />
                            Search and Rescue
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-700">
                        <Button
                          onClick={handleSave}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          title="Save changes"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button
                          onClick={() => removeHelicopter(helicopter.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                          title="Delete entry"
                        >
                          <Trash className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Drones Card */}
        <div className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-orange-500">Drones</h3>
            <div className="flex gap-2">
              <Button onClick={handleSave} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" /> Save Drones
              </Button>
              <Button onClick={addDrone} size="sm" className="bg-orange-600 hover:bg-orange-700" data-testid="add-drone-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Drone
              </Button>
            </div>
          </div>

          {(formData.drones || []).length === 0 ? (
            <p className={`${theme.colors.textMuted} text-center py-4`}>No drones added yet</p>
          ) : (
            <div className="space-y-3">
              {(formData.drones || []).map((drone) => (
                <div key={drone.id} className="p-3 bg-gray-800/50 rounded">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <Label className="text-white text-xs mb-2 block">Photo</Label>
                      <div
                        className="relative w-32 h-32 bg-gray-700 rounded border-2 border-dashed border-gray-600 hover:border-orange-500/60 flex items-center justify-center overflow-hidden cursor-pointer transition-colors"
                        title="Drag & drop an image here, or click Upload"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-orange-500', 'ring-2', 'ring-orange-500'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500', 'ring-2', 'ring-orange-500'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-orange-500', 'ring-2', 'ring-orange-500');
                          const file = e.dataTransfer.files?.[0];
                          if (file) handleDroneImageUpload(drone.id, { target: { files: [file] } });
                        }}
                        data-testid={`drone-image-dropzone-${drone.id}`}
                      >
                        {drone.image ? (
                          <img src={drone.image} alt="Drone" className="w-full h-full object-cover pointer-events-none" />
                        ) : (
                          <div className="text-center pointer-events-none">
                            <Camera className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                            <span className="text-xs text-gray-400">Drag & drop<br/>or upload</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 mt-2">
                        <button type="button" onClick={() => document.getElementById(`drone-upload-${drone.id}`).click()} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded text-xs flex items-center justify-center gap-1" title="Upload from gallery">
                          <Upload className="h-3 w-3" /> Upload
                        </button>
                        <button type="button" onClick={() => handleDroneCameraCapture(drone.id)} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded text-xs flex items-center justify-center gap-1" title="Take photo with camera">
                          <Camera className="h-3 w-3" /> Camera
                        </button>
                      </div>
                      <input id={`drone-upload-${drone.id}`} type="file" accept="image/*" onChange={(e) => handleDroneImageUpload(drone.id, e)} className="hidden" />
                    </div>

                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
                        <div>
                          <Label className="text-white text-xs">Registration</Label>
                          <Input value={drone.registration} onChange={(e) => updateDrone(drone.id, 'registration', e.target.value)} className="bg-gray-800 border-gray-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Make/Model</Label>
                          <Input value={drone.make_model} onChange={(e) => updateDrone(drone.id, 'make_model', e.target.value)} className="bg-gray-800 border-gray-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Base</Label>
                          <Input value={drone.base} onChange={(e) => updateDrone(drone.id, 'base', e.target.value)} className="bg-gray-800 border-gray-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Start Date</Label>
                          <Input type="date" value={drone.start ? drone.start.split('T')[0] : ''} onChange={(e) => {
                            const date = e.target.value;
                            const time = drone.start ? drone.start.split('T')[1] : '00:00';
                            updateDrone(drone.id, 'start', `${date}T${time}`);
                          }} className="bg-gray-800 border-gray-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Start Time</Label>
                          <Input type="text" placeholder="23:35" pattern="^([01]\d|2[0-3]):?([0-5]\d)$" maxLength="5" value={drone.start ? drone.start.split('T')[1] : ''} onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9:]/g, '');
                            if (value.length === 2 && !value.includes(':')) value = value + ':';
                            if (value.length <= 5) {
                              const date = drone.start ? drone.start.split('T')[0] : '';
                              updateDrone(drone.id, 'start', `${date}T${value}`);
                            }
                          }} className="bg-gray-800 border-gray-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-white text-xs">Remarks</Label>
                          <Input value={drone.remarks} onChange={(e) => updateDrone(drone.id, 'remarks', e.target.value)} className="bg-gray-800 border-gray-700 text-white" />
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-white text-xs">Tasking</Label>
                          <Input value={drone.tasking || ''} onChange={(e) => updateDrone(drone.id, 'tasking', e.target.value)} className="bg-gray-800 border-gray-700 text-white w-full" data-testid={`drone-tasking-${drone.id}`} />
                        </div>
                        <div className="md:col-span-6">
                          <Label className="text-white text-xs">Capabilities</Label>
                          <Input value={drone.capabilities || ''} onChange={(e) => updateDrone(drone.id, 'capabilities', e.target.value)} className="bg-gray-800 border-gray-700 text-white w-full" data-testid={`drone-capabilities-${drone.id}`} />
                        </div>
                      </div>

                      <div className="flex gap-4 flex-wrap items-center justify-between">
                        <div className="flex gap-4 flex-wrap">
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox checked={drone.military} onCheckedChange={(c) => updateDrone(drone.id, 'military', c)} /> Military
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox checked={drone.civilian} onCheckedChange={(c) => updateDrone(drone.id, 'civilian', c)} /> Civilian
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox checked={drone.stars} onCheckedChange={(c) => updateDrone(drone.id, 'stars', c)} /> STARS
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox checked={drone.medevac} onCheckedChange={(c) => updateDrone(drone.id, 'medevac', c)} /> Medevac
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox checked={drone.casevac} onCheckedChange={(c) => updateDrone(drone.id, 'casevac', c)} /> Casevac
                          </label>
                          <label className="flex items-center gap-2 text-white text-sm">
                            <Checkbox checked={drone.search_rescue} onCheckedChange={(c) => updateDrone(drone.id, 'search_rescue', c)} /> Search and Rescue
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-700">
                        <Button onClick={handleSave} size="sm" className="bg-green-600 hover:bg-green-700" title="Save changes">
                          <Save className="h-4 w-4 mr-2" /> Save
                        </Button>
                        <Button onClick={() => removeDrone(drone.id)} size="sm" variant="outline" className="border-red-500/50 text-red-500 hover:bg-red-500/10" title="Delete entry">
                          <Trash className="h-4 w-4 mr-2" /> Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingSummary(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-orange-600 hover:bg-orange-700"
            disabled={Object.keys(errors).length > 0}
          >
            <Save className="h-4 w-4 mr-2" />
            {editingSummary ? 'Update' : 'Save'} ICS 220
          </Button>
        </div>
      </div>
    </div>
  );
};

// After Action Report Section for ICS Planning
// AfterActionReportSection moved to components/AfterActionReport.js

// ICS 221 - Demobilization Checkout Form

export default ICS220Form;
